/**
 * Project store: load, title/bin autosave, and the sync connection.
 *
 * The timeline is NOT autosaved. It lives in the CRDT room owned by the sync
 * service, which snapshots it back to `Project.timeline` on a debounce; the
 * backend refuses a timeline PATCH outright (409) while sync is enabled. Only
 * title and description still go over HTTP.
 */
import { create } from 'zustand';
import { backend, type Project } from '../api/backend';
import { connectTimelineSync, type ConnectionStatus, type SyncHandle } from '../crdt/provider';
import { useBin } from './bin';
import { attachChat, detachChat } from './chat';
import { attachAgent, detachAgent } from './agent';
import { attachPresence, detachPresence } from './presence';

export type SaveState = 'saved' | 'dirty' | 'saving' | 'error' | 'offline';

const AUTOSAVE_MS = 1500;

interface ProjectState {
  project: Project | null;
  loading: boolean;
  loadError: string | null;
  saveState: SaveState;
  /**
   * Timeline sync status. The editor must not be usable until this is 'synced':
   * before that the document has not arrived and an empty timeline would be
   * indistinguishable from an empty project.
   */
  connection: ConnectionStatus;
  load(id: string): Promise<void>;
  /** Join via a collab link: no owner payload, project identified by token. */
  loadCollab(token: string): Promise<void>;
  unload(): void;
  setTitle(title: string): void;
  /** Reflect a publish/unpublish result in the loaded project (no autosave). */
  setPublish(isPublic: boolean, publicToken?: string): void;
  /** Reflect a collab enable/disable result (no autosave). */
  setCollab(enabled: boolean, collabToken?: string): void;
  saveNow(): Promise<void>;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let sync: SyncHandle | null = null;

/**
 * Which load is current.
 *
 * `load`/`loadCollab` await the network before connecting, and React can
 * unmount and remount in between (StrictMode does exactly this). Without a
 * generation check the abandoned load still calls `startSync` when its request
 * finally resolves, leaving a second websocket open and broadcasting presence.
 * Every post-await step checks that it is still the current load.
 */
let loadGeneration = 0;

/** Tear down everything the sync connection owns, in one place. */
function teardownSync(): void {
  detachPresence();
  detachChat();
  detachAgent();
  sync?.destroy();
  sync = null;
}

function startSync(projectId: number | string, token?: string): void {
  // Full teardown, not just `sync?.destroy()`: otherwise presence keeps a
  // reference to a destroyed provider's awareness and chat observes a dead
  // document until the attach calls below happen to replace them.
  teardownSync();
  useProject.setState({ connection: 'connecting' });
  const generation = loadGeneration;
  sync = connectTimelineSync({
    projectId,
    token,
    onStatus: (connection) => {
      // A late event from a superseded connection must not overwrite the
      // current one's status.
      if (generation !== loadGeneration) return;
      useProject.setState({ connection });
    },
  });
  attachPresence(sync.awareness);
  attachChat(sync.doc);
  attachAgent(sync.doc);
}

export const useProject = create<ProjectState>((set, get) => ({
  project: null,
  loading: false,
  loadError: null,
  saveState: 'saved',
  connection: 'connecting',

  async load(id) {
    get().unload();
    const generation = ++loadGeneration;
    set({ loading: true, loadError: null, saveState: 'saved' });
    try {
      const project = await backend.projects.get(id);
      if (generation !== loadGeneration) return; // superseded while in flight
      useBin.getState().load(project.id, project.bin ?? []);
      set({ project, loading: false });
      // The timeline in this payload is only the sync service's last snapshot;
      // the live document arrives over the socket. Do NOT load it into the
      // store, or the client would seed its own doc and diverge from the room.
      startSync(project.id);
    } catch (e) {
      if (generation !== loadGeneration) return;
      set({
        loading: false,
        loadError: e instanceof Error ? e.message : 'Failed to load project',
      });
    }
  },

  async loadCollab(token) {
    get().unload();
    const generation = ++loadGeneration;
    set({ loading: true, loadError: null, saveState: 'saved' });
    try {
      const project = await backend.collab.join(token);
      if (generation !== loadGeneration) return; // superseded while in flight
      useBin.getState().load(project.id, project.bin ?? []);
      set({ project: project as unknown as Project, loading: false });
      startSync(project.id, token);
    } catch (e) {
      if (generation !== loadGeneration) return;
      set({
        loading: false,
        loadError: e instanceof Error ? e.message : 'This collaboration link is not valid',
      });
    }
  },

  unload() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = null;
    // Invalidate any load still in flight so it cannot reconnect behind us.
    loadGeneration++;
    teardownSync();
    useBin.getState().unload();
    set({ project: null, saveState: 'saved', loadError: null, connection: 'connecting' });
  },

  setTitle(title) {
    const { project } = get();
    if (!project) return;
    set({ project: { ...project, title } });
    markDirty();
  },

  setPublish(isPublic, publicToken) {
    const { project } = get();
    if (!project) return;
    set({
      project: {
        ...project,
        is_public: isPublic,
        public_token: publicToken ?? project.public_token,
      },
    });
  },

  setCollab(enabled, collabToken) {
    const { project } = get();
    if (!project) return;
    set({
      project: {
        ...project,
        collab_enabled: enabled,
        collab_token: collabToken ?? project.collab_token,
      },
    });
  },

  async saveNow() {
    const { project } = get();
    if (!project) return;
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = null;
    set({ saveState: 'saving' });
    try {
      // Title only. The timeline belongs to the sync room, and the backend
      // returns 409 for a timeline PATCH while sync is enabled.
      const saved = await backend.projects.patch(project.id, {
        title: project.title,
      });
      set((s) => ({
        // keep any newer local title
        project: s.project ? { ...saved, title: s.project.title } : saved,
        saveState: s.saveState === 'saving' ? 'saved' : s.saveState,
      }));
    } catch {
      set({ saveState: 'error' });
    }
  },
}));

function markDirty() {
  useProject.setState({ saveState: 'dirty' });
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    void useProject.getState().saveNow();
  }, AUTOSAVE_MS);
}
